import { DgtPdOrderPicklist, OrderType } from '../models'
import Badge from './Badge'
import { formatNumber, formatDate, formatDateFromUnix } from '../utilities/Format'
import { memo, useMemo } from 'react'
import ApprovedLabel from '../components/ApprovedLabel'
import { PicklistStatus } from '../constants/EnumType'
import { useFormContext } from 'react-hook-form'
import { ExtraReturnStatus } from '../models'
type HeaderDetailProps = {
  title?: string
  header?: DgtPdOrderPicklist,
  isShowRelease?: boolean,
  isInputKi?: boolean,
  isShowRemark?: boolean,
  readOnly?: boolean,
  isPdNotApprove?: boolean,
}

const Label = ({ label, value } : { label: string, value?: string | number }) => 
  <div className='flex'>
    <h6 className="font-bold basis-1/3 lg:basis-1/4">{label}</h6>
    <h6 className="font-light break-words basis-2/3 lg:basis-3/4">{value}</h6>
  </div>

const KiInput = memo(( { readOnly } : { readOnly : boolean }) => {
  const { register, formState } = useFormContext();

  return <div className='flex items-center'>
    <h6 className="font-light basis-1/3 lg:basis-1/4">
      Ref No. <span className='text-sm text-muted'>(ไม่เกิน 20 ตัวอักษร)</span>
    </h6>
    <input placeholder='กรอก Ref NO. ก่อนอนุมัติ'
      maxLength={20}
      disabled={readOnly}
      className={`input-form-text basis-1/3 lg:basis-2/4 ${formState.errors.kiNo ? ' border-red-500':''}`}
      {...register('kiNo')}
    />
    {formState.errors.kiNo ? <small className='ml-2 text-red-500'>{formState.errors.kiNo.message}</small> : null}
  </div>
})

const HeaderDetail = ({
  title,
  header,
  isShowRelease = true,
  isInputKi = false,
  isShowRemark = false,
  readOnly = false,
  isPdNotApprove = false
}: HeaderDetailProps) => {
  const isTrialBatch = header && header.trialBatch
  const checkedInfo = useMemo(() => {
    const paths = window.location.pathname.split('/').filter(p => p)

    if (paths[0] === 'good-issue-order' && header && header.approvedDateTime) return {
      by: `อนุมัติจ่ายโดย ${header.approvedBy}`,
      datetime: header.approvedDateTime
    }
    if (paths[0] === 'picklist-approve' && header && header.requestedDateTime) return {
      by: `อนุมัติเบิกโดย ${header.requestedBy}`,
      datetime: header.requestedDateTime
    }
    if (paths[0] === 'picklist-check' && header && header.checkedDateTime) return {
      by: `PD ตรวจ Complete โดย ${header.checkedBy}`,
      datetime: header.checkedDateTime
    }
    if (paths[0] === 'picklist-qa-check' && header && header.qualityCheckedDateTime) return {
      by: `PD ตรวจ โดย Mixing ${header.qualityCheckedBy}`,
      datetime: header.qualityCheckedDateTime
    }
    if (paths[0] === 'picklist-complete' && header && header.completedDateTime) return {
      by: `ปิดเอกสาร/อนุมัติคืน โดย ${header.completedBy}`,
      datetime: header.completedDateTime
    }
    return null
  }, [ header ])

  const isWaitShortage = (header && header.status === PicklistStatus.WH_SHORTAGE)

  const isCanceled = (header && header.status === ExtraReturnStatus.CANCELED)

  return <>
    <div className='flex items-center justify-between gap-2'>
      <div>
        <h3 className={`my-3`}>{title}</h3>
        {isTrialBatch ? <h6><Badge variant='text-white font-bold bg-yellow-500 text-base'>{header.trialBatch!}</Badge></h6> : null}
        {header?.orderType && header?.orderType === OrderType.REPACK && <h6>
          <Badge variant={` text-white bg-blue-500`}>{`${header?.orderType}${header?.alternativeText ? ` - ${header?.alternativeText}` : ''}`}</Badge>
        </h6>}
        {header?.orderType && header?.orderType === OrderType.CLAIM && <h6>
          <Badge variant={` text-white bg-red-500`}>{`${header?.orderType}${header?.alternativeText ? ` - ${header?.alternativeText}` : ''}`}</Badge></h6>}
      </div>
      <div className='space-y-1 '>
        {checkedInfo ? <ApprovedLabel message={`${checkedInfo.by} / ${formatDateFromUnix(checkedInfo.datetime)}`} /> : null}
        {isWaitShortage ? <h6><Badge variant='outline-primary'>อนุมัติบางส่วน รอของเข้า</Badge></h6> : null}
        {isCanceled ? <h6><Badge variant='bg-gray-900 text-white text-sm font-light'>ยกเลิกแล้ว</Badge></h6> : null}
      </div>
    </div>
    <div className='grid grid-cols-1 gap-2 md:grid-cols-2'>
      <Label label="Mat Code" value={`${header?.materialCode}${header?.barcodeFgCase ? ` (${header?.barcodeFgCase})` : ''}`} />
      <Label label="Batch No" value={header?.batchLotNo!} />
      <div className='flex'>
        <h6 className="font-bold basis-1/3 lg:basis-1/4">Mat Description</h6>
        <div className="basis-2/3 lg:basis-3/4">
          <h6 className='font-light break-words '>{header?.materialDescription}</h6>
          <div className='flex flex-wrap gap-1'>
            {header?.infoFwt && <Badge variant='product-info'>{header?.infoFwt}</Badge>}
            {header?.infoEffBulkDate && <Badge variant='product-info'>{`Eff.Bulk : ${header?.infoEffBulkDate}`}</Badge>}
            {header?.infoRefPkg && <Badge variant='product-info'>{header?.infoRefPkg}</Badge>}
            {header?.infoPallet && <Badge variant='product-info'>{`Pallet: ${header?.infoPallet}`}</Badge>}
            {header?.infoPacking && <Badge variant='product-info'>{`Packing: ${header?.infoPacking}`}</Badge>}
          </div>
        </div>
      </div>
      <Label label="Order Qty" value={`${formatNumber(header?.orderQty!)} ${header?.materialUnit || ''}`} />
      {isShowRelease ? <Label label="Released Date" value={formatDate(header?.releasedDate!)} /> : null}
      {isShowRelease ? <Label label="Released By" value={header?.releasedBy!} /> : null}
      {isInputKi ? <KiInput readOnly={readOnly} /> : null}
      {isShowRemark || isPdNotApprove ? <Label label="หมายเหตุ" value={isPdNotApprove ? 'รอ PD อนุมัติเบิก' : header?.commentText!} /> : null}
    </div>
  </>
}

export default memo(HeaderDetail)